/**
 * Detects consonants bearing kamatz katan (U+05C7) or shewa na (vocal shewa)
 * in Hebrew words. Used by the auto-color-vowels feature.
 *
 * Kamatz katan: detected by scanning for U+05C7 (already encoded in Sefaria's
 * "Miqra according to the Masorah" text).
 *
 * Shewa na: detected via havarotjs syllabification. A shewa is na (vocal) when
 * its cluster is the sole cluster in a syllable — it opens a new syllable rather
 * than closing a previous one.
 */
import { Text } from 'havarotjs'

import { isCantillation, isCharacter } from '@/util/CharacterClasses'

const KAMATZ_KATAN = '\u05C7'

/**
 * Strips cantillation marks (ta'amim) from a Hebrew string.
 * Vowels and consonants are preserved.
 */
function stripCantillation(text: string): string {
  return [...text].filter((c) => !isCantillation(c)).join('')
}

/**
 * Returns 0-based consonant indices within `wordText` that bear kamatz katan (U+05C7).
 * The index counts only consonant characters (non-vowel, non-cantillation).
 */
function getKamatzKatanConsonants(wordText: string): number[] {
  const positions: number[] = []
  const chars = [...wordText]
  let consonantIndex = 0
  let lastConsonantIndex = -1

  for (const char of chars) {
    if (isCharacter(char)) {
      lastConsonantIndex = consonantIndex
      consonantIndex++
    } else if (char === KAMATZ_KATAN && lastConsonantIndex >= 0) {
      if (!positions.includes(lastConsonantIndex)) {
        positions.push(lastConsonantIndex)
      }
    }
  }
  return positions
}

/**
 * Returns 0-based consonant indices within `wordText` that carry shewa na (vocal shewa).
 * Uses havarotjs syllabification: a shewa is na when its cluster is the only cluster
 * in the syllable (it opens a new syllable rather than closing a previous one).
 *
 * Cantillation marks are stripped before analysis since havarotjs expects nikud-only text.
 */
function getShewaNaConsonants(wordText: string): number[] {
  const stripped = stripCantillation(wordText)
  if (!stripped) {
    return []
  }

  try {
    const text = new Text(stripped)
    if (!text.words.length) {
      return []
    }

    const hWord = text.words[0]
    const positions: number[] = []
    let consonantIndex = 0

    for (const syllable of hWord.syllables) {
      for (const cluster of syllable.clusters) {
        const clusterConsonantStart = consonantIndex
        for (const ch of cluster.chars) {
          if (ch.isConsonant) {
            consonantIndex++
          }
        }
        if (cluster.hasSheva && syllable.clusters.length === 1 && clusterConsonantStart > 0) {
          positions.push(clusterConsonantStart)
        }
      }
    }
    return positions
  } catch {
    return []
  }
}

export { getKamatzKatanConsonants, getShewaNaConsonants, KAMATZ_KATAN, stripCantillation }
